/**
 * Application page components organized by feature domain. Lazy-loaded for code splitting.
 * @agentic connections
 */

// Utils
// `lazyWithRetry` wraps each `import()` with stale-chunk recovery so
// clinicians on an old tab after a redeploy don't hit
// "Failed to fetch dynamically imported module" on navigation.
import { lazyWithRetry as lazy } from "@/utils/ui/lazyWithRetry";

// Appointment Pages
/** Lazy-loaded appointments page. @agentic connections */
export const AppointmentsPage = lazy(() =>
  import("./appointment/AppointmentsPage").then((module) => ({
    default: module.AppointmentsPage,
  })),
);

// Healthcare Service Pages
/** Lazy-loaded healthcare service detail page. @agentic connections */
export const HealthcareServiceDetailPage = lazy(() =>
  import("./healthcare-service/HealthcareServiceDetailPage").then((module) => ({
    default: module.HealthcareServiceDetailPage,
  })),
);

/** Lazy-loaded healthcare services list page. @agentic connections */
export const HealthcareServicesPage = lazy(() =>
  import("./healthcare-service/HealthcareServicesPage").then((module) => ({
    default: module.HealthcareServicesPage,
  })),
);

// Library (Clinical Content Management) Pages
/** Lazy-loaded libraries list page. @agentic connections */
export const LibrariesPage = lazy(() =>
  import("./library/LibrariesPage").then((module) => ({
    default: module.LibrariesPage,
  })),
);

/** Lazy-loaded library detail page. @agentic connections */
export const LibraryDetailPage = lazy(() =>
  import("./library/LibraryDetailPage").then((module) => ({
    default: module.LibraryDetailPage,
  })),
);

/** Lazy-loaded library create/edit page. @agentic connections */
export const LibraryFormPage = lazy(() =>
  import("./library/LibraryFormPage").then((module) => ({
    default: module.LibraryFormPage,
  })),
);

// Not Found Page
/** Lazy-loaded 404 page. @agentic connections */
export const NotFoundPage = lazy(() =>
  import("./not-found/NotFoundPage").then((module) => ({
    default: module.NotFoundPage,
  })),
);

// Organization Pages
/** Lazy-loaded organization detail page. @agentic connections */
export const OrganizationDetailPage = lazy(() =>
  import("./organization/OrganizationDetailPage").then((module) => ({
    default: module.OrganizationDetailPage,
  })),
);

/** Lazy-loaded organizations list page. @agentic connections */
export const OrganizationsPage = lazy(() =>
  import("./organization/OrganizationsPage").then((module) => ({
    default: module.OrganizationsPage,
  })),
);

// Insurance Plan Pages
/** Lazy-loaded insurance plans page. @agentic connections */
export const InsurancePlansPage = lazy(() =>
  import("./insurance-plan/InsurancePlansPage").then((module) => ({
    default: module.InsurancePlansPage,
  })),
);

// Patient Pages
/** Lazy-loaded patient profile page. @agentic connections */
export const PatientPage = lazy(() =>
  import("./patient/PatientPage").then((module) => ({
    default: module.PatientPage,
  })),
);

/** Lazy-loaded patient resource detail page. @agentic connections */
export const PatientResourceDetailPage = lazy(() =>
  import("./patient/PatientResourceDetailPage").then((module) => ({
    default: module.PatientResourceDetailPage,
  })),
);

/** Lazy-loaded patient resource list page. @agentic connections */
export const PatientResourcePage = lazy(() =>
  import("./patient/PatientResourcePage").then((module) => ({
    default: module.PatientResourcePage,
  })),
);

/** Lazy-loaded patients list page. @agentic connections */
export const PatientsPage = lazy(() =>
  import("./patient/PatientsPage").then((module) => ({
    default: module.PatientsPage,
  })),
);

/** Lazy-loaded patient timeline page. @agentic connections */
export const PatientTimelinePage = lazy(() =>
  import("./patient/PatientTimelinePage").then((module) => ({
    default: module.PatientTimelinePage,
  })),
);

// Practitioner Role Pages
/** Lazy-loaded practitioner role detail page. @agentic connections */
export const PractitionerRoleDetailPage = lazy(() =>
  import("./practitioner-role/PractitionerRoleDetailPage").then((module) => ({
    default: module.PractitionerRoleDetailPage,
  })),
);

/** Lazy-loaded practitioner roles list page. @agentic connections */
export const PractitionerRolesPage = lazy(() =>
  import("./practitioner-role/PractitionerRolesPage").then((module) => ({
    default: module.PractitionerRolesPage,
  })),
);

// Sign In Page
/** Lazy-loaded sign-in page. @agentic connections */
export const SignInPage = lazy(() =>
  import("./signin/SignInPage").then((module) => ({
    default: module.SignInPage,
  })),
);

// Triage Pages
/** Lazy-loaded triage dashboard page. @agentic connections */
export const TriageDashboardPage = lazy(() =>
  import("./triage/TriageDashboardPage").then((module) => ({
    default: module.TriageDashboardPage,
  })),
);

// Task Pages
/** Lazy-loaded tasks page. @agentic connections */
export const TasksPage = lazy(() =>
  import("./task/TasksPage").then((module) => ({
    default: module.TasksPage,
  })),
);
